import { useSuspenseInfiniteQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { FileRecord } from "@/shared/domain/files";
import { PaginatedResponse } from "@/shared/http";

interface UsePaginatedDatasetFilesOptions {
  datasetId: string | undefined;
  includePatterns?: string[];
  limit?: number;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
}

export function getQueryKey(options: UsePaginatedDatasetFilesOptions) {
  const { datasetId, includePatterns, limit, sortBy, sortDirection } = options;
  return [
    "datasetFiles",
    datasetId,
    includePatterns,
    limit,
    sortBy,
    sortDirection,
  ];
}

/**
 * Hook to fetch files for a dataset with pagination using Suspense.
 *
 * This hook uses React Query's useSuspenseInfiniteQuery which integrates with
 * React Suspense for loading states and Error Boundaries for error handling.
 * The component using this hook should be wrapped in a Suspense boundary with
 * a fallback UI for loading states, and an Error Boundary for error handling.
 *
 * Uses datasetService.listFiles() for paginated fetching with optional
 * pattern-based filtering. Directories are automatically filtered out.
 * Files are sorted by creation date (newest first) by default.
 *
 * @param options - Configuration options
 * @param options.datasetId - The ID of the dataset to fetch files for
 * @param options.includePatterns - Optional gitignore-style patterns to filter files
 * @param options.limit - Optional page size limit
 * @param options.sortBy - Optional field to sort by (default: "created")
 * @param options.sortDirection - Optional sort direction (default: "DESC")
 *
 * @example
 * ```tsx
 * function FileList({ datasetId, searchTerm }) {
 *   // Convert search term to include pattern
 *   const includePatterns = searchTerm ? [`**\/*${searchTerm}*`] : undefined;
 *
 *   const filesQuery = useInfiniteSuspenseDatasetFilesQuery({
 *     datasetId,
 *     includePatterns,
 *   });
 *
 *   return (
 *     <div>
 *       {filesQuery.data.map(file => (
 *         <div key={file.file_id}>{file.name}</div>
 *       ))}
 *       {filesQuery.hasNextPage && (
 *         <button onClick={() => filesQuery.fetchNextPage()}>
 *           Load More
 *         </button>
 *       )}
 *     </div>
 *   );
 * }
 *
 * // Usage with Suspense and ErrorBoundary
 * <ErrorBoundary fallback={<ErrorUI />}>
 *   <Suspense fallback={<LoadingUI />}>
 *     <FileList datasetId="ds_123" searchTerm="sensor" />
 *   </Suspense>
 * </ErrorBoundary>
 * ```
 */
export function useInfiniteSuspenseDatasetFilesQuery(
  options: UsePaginatedDatasetFilesOptions,
) {
  const { datasetService } = useDomainServices();
  const {
    datasetId,
    includePatterns,
    limit,
    sortBy = "created",
    sortDirection = "DESC",
  } = options;

  return useSuspenseInfiniteQuery({
    queryKey: getQueryKey(options),
    queryFn: async ({
      pageParam,
      signal,
    }): Promise<PaginatedResponse<FileRecord>> => {
      if (!datasetId) {
        return {
          items: [],
          next_token: null,
          total_count: null,
        };
      }
      return datasetService.listFiles(datasetId, {
        abortSignal: signal,
        includePatterns,
        limit,
        pageToken: pageParam,
        sortBy,
        sortDirection,
      });
    },
    select: (data): FileRecord[] => {
      // Flatten pages into single array
      return data.pages.flatMap((page) => page.items);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedResponse<FileRecord>) =>
      lastPage.next_token ?? undefined,
    // Shorter garbage collection time for search queries (1 minute)
    gcTime: 60 * 1000,
  });
}
